import { el, fmt } from '../../lib/dom.js';
import type { KatalogResponse, User } from '../../lib/api.js';
import {
  ladeProtokoll,
  onSaveState,
  registriereOnlineSync,
  resetSaveListeners,
  speichern,
  summen,
  type SaveState,
} from '../../lib/state.js';
import { renderAbschluss } from './step-abschluss.js';
import { renderBauteile } from './step-bauteile.js';
import { renderErgebnis } from './step-ergebnis.js';
import { renderGrunddaten } from './step-grunddaten.js';
import { renderPositionen } from './step-positionen.js';
import { STEPS, type WizardCtx } from './types.js';

const SAVE_LABEL: Record<SaveState, string> = {
  gespeichert: 'Gespeichert ✓',
  speichert: 'Speichert …',
  offline: 'Offline – lokal gesichert',
  fehler: 'Speichern fehlgeschlagen',
};

export async function renderWizard(
  root: HTMLElement,
  id: string,
  user: User,
  katalog: KatalogResponse,
): Promise<void> {
  const p = await ladeProtokoll(id);
  let step = 0;
  resetSaveListeners();

  document.body.classList.add('hat-navbar');
  root.replaceChildren();

  /* ── Kopf ── */
  const topbar = el('header', { class: 'topbar' });
  const zurueck = el(
    'button',
    { class: 'linkbtn', type: 'button', 'aria-label': 'Zur Übersicht' },
    '←',
  );
  zurueck.addEventListener('click', () => {
    location.hash = '';
  });
  const brand = el('div', { class: 'brand' }, 'Wertermittlung');
  const badge = el('small', { class: 'savebadge' }, SAVE_LABEL[p.saveState]);
  brand.append(badge);
  const total = el('div', { class: 'total' });
  total.append(el('small', {}, 'Entschädigung'));
  const totalWert = el('strong', {}, fmt(0));
  total.append(totalWert);
  topbar.append(zurueck, brand, total);
  onSaveState((s) => {
    badge.textContent = SAVE_LABEL[s];
    badge.className = 'savebadge' + (s === 'offline' || s === 'fehler' ? ' offline' : '');
  });

  /* ── Stepper & Inhalt ── */
  const stepper = el('nav', { class: 'stepper', 'aria-label': 'Schritte' });
  const main = el('main');
  const navbar = el('footer', { class: 'navbar' });
  const prev = el('button', { class: 'btn', type: 'button' }, '← Zurück');
  const hint = el('div', { class: 'spacer' });
  const next = el('button', { class: 'btn btn-primary', type: 'button' }, 'Weiter →');
  navbar.append(prev, hint, next);
  root.append(topbar, stepper, main, navbar);

  const stepDone = (i: number): boolean => {
    const d = p.data;
    const s = summen(p);
    switch (i) {
      case 0:
        return Boolean(d.grund.parzelle && d.grund.paechter);
      case 1:
        return d.bauteile.some((b) => (b.flaeche ?? 0) > 0);
      case 2:
        return d.pos.neben.length > 0;
      case 3:
        return d.pos.kulturen.length > 0;
      case 4:
        return d.pos.zier.length > 0;
      case 5:
        return s.entschaedigung > 0;
      case 6:
        return d.sigs.some((x) => x.img);
      default:
        return false;
    }
  };

  const ctx: WizardCtx = {
    p,
    katalog,
    user,
    recalc() {
      totalWert.textContent = fmt(summen(p).entschaedigung);
      renderStepper();
    },
    save() {
      speichern(p);
    },
    rerender() {
      renderStep();
    },
    go(i: number) {
      step = Math.max(0, Math.min(STEPS.length - 1, i));
      renderStep();
      window.scrollTo({ top: 0 });
    },
  };

  function renderStepper(): void {
    stepper.replaceChildren();
    STEPS.forEach((titel, i) => {
      const done = stepDone(i);
      const chip = el('button', {
        class: 'stepchip' + (i === step ? ' active' : '') + (done ? ' done' : ''),
        type: 'button',
      });
      chip.append(
        el('span', { class: 'num' }, done && i !== step ? '✓' : String(i + 1)),
        document.createTextNode(titel),
      );
      chip.addEventListener('click', () => ctx.go(i));
      stepper.append(chip);
    });
    const active = stepper.querySelector<HTMLElement>('.active');
    if (active)
      stepper.scrollLeft = Math.max(
        0,
        active.offsetLeft - (stepper.clientWidth - active.offsetWidth) / 2,
      );
  }

  function renderStep(): void {
    main.replaceChildren();
    main.className = p.readonly ? 'readonly' : '';
    if (p.readonly) {
      main.append(
        el(
          'div',
          { class: 'readonly-banner' },
          'Dieses Protokoll ist abgeschlossen und unveränderlich. Das PDF kann jederzeit erneut erzeugt werden (Schritt 7).',
        ),
      );
    }
    const renderers: ((c: WizardCtx, container: HTMLElement) => void)[] = [
      renderGrunddaten,
      renderBauteile,
      (c, m) => renderPositionen(c, m, 'neben'),
      (c, m) => renderPositionen(c, m, 'kulturen'),
      (c, m) => renderPositionen(c, m, 'zier'),
      renderErgebnis,
      renderAbschluss,
    ];
    renderers[step]?.(ctx, main);
    hint.textContent = `Schritt ${step + 1} von ${STEPS.length}`;
    prev.disabled = step === 0;
    next.textContent = step === STEPS.length - 1 ? 'Zur Übersicht ✓' : 'Weiter →';
    renderStepper();
    ctx.recalc();
  }

  prev.addEventListener('click', () => ctx.go(step - 1));
  next.addEventListener('click', () => {
    if (step === STEPS.length - 1) {
      location.hash = '';
      return;
    }
    ctx.go(step + 1);
  });

  registriereOnlineSync(p);
  renderStep();
}
